import React, { useState, useEffect } from 'react';
import { pqrService, handleApiError } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  nuevo: { label: 'Nuevo', color: 'bg-blue-100 text-blue-800' },
  en_progreso: { label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800' },
  esperando_cliente: { label: 'Esperando Cliente', color: 'bg-purple-100 text-purple-800' },
  resuelto: { label: 'Resuelto', color: 'bg-green-100 text-green-800' },
  escalado: { label: 'Escalado', color: 'bg-red-100 text-red-800' },
  cerrado: { label: 'Cerrado', color: 'bg-gray-100 text-gray-800' },
};

const RESOLUTION_TYPES = [
  { value: 'reenvio', label: 'Reenvío de producto' },
  { value: 'nota_credito', label: 'Nota crédito' },
  { value: 'devolucion', label: 'Devolución' },
  { value: 'reemplazo', label: 'Reemplazo' },
  { value: 'disculpa', label: 'Disculpa / Gestión' },
  { value: 'descuento_futuro', label: 'Descuento futuro' },
  { value: 'sin_accion', label: 'Sin acción requerida' },
  { value: 'otro', label: 'Otro' },
];

export default function PQRDetailModal({ isOpen, onClose, ticketId, onUpdated }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [comment, setComment] = useState('');
  const [resolution, setResolution] = useState({ type: '', notes: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && ticketId) loadTicket();
  }, [isOpen, ticketId]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      const res = await pqrService.getById(ticketId);
      if (res?.data) {
        setTicket(res.data);
        setNewStatus(res.data.status);
        setResolution({ type: res.data.resolution_type || '', notes: res.data.resolution_notes || '' });
      }
    } catch (err) {
      toast.error(handleApiError(err, 'Error cargando PQR'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      const payload = {};
      if (newStatus !== ticket.status) payload.status = newStatus;
      if (resolution.type) payload.resolution_type = resolution.type;
      if (resolution.notes) payload.resolution_notes = resolution.notes;
      if (comment) payload.note = comment;

      await pqrService.update(ticketId, payload);

      if (comment && !payload.status) {
        await pqrService.addComment(ticketId, { note: comment });
      }

      toast.success('PQR actualizado');
      setComment('');
      onUpdated?.();
      loadTicket();
    } catch (err) {
      toast.error(handleApiError(err, 'Error actualizando PQR'));
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  const Badge = ({ status }) => {
    const s = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : ticket ? (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">PQR #{ticket.id}</h2>
                <p className="text-sm opacity-80">
                  {ticket.order_number ? `Pedido ${ticket.order_number}` : 'Sin pedido asociado'}
                  {ticket.customer_name && ` • ${ticket.customer_name}`}
                </p>
              </div>
              <Badge status={ticket.status} />
            </div>

            <div className="p-6 space-y-5">
              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-gray-500 block">Categoría</span><span className="font-medium capitalize">{ticket.category}</span></div>
                <div><span className="text-gray-500 block">Subtipo</span><span className="font-medium">{ticket.subcategory || '-'}</span></div>
                <div><span className="text-gray-500 block">Prioridad</span><span className="font-medium capitalize">{ticket.priority}</span></div>
                <div><span className="text-gray-500 block">Impacto</span><span className="font-medium">${Number(ticket.financial_impact || 0).toLocaleString()}</span></div>
                <div><span className="text-gray-500 block">Creado</span><span className="font-medium">{new Date(ticket.created_at).toLocaleDateString('es-CO')}</span></div>
                <div><span className="text-gray-500 block">Fuente</span><span className="font-medium capitalize">{ticket.source}</span></div>
                <div><span className="text-gray-500 block">Asignado a</span><span className="font-medium">{ticket.assignee_name || 'Sin asignar'}</span></div>
                <div><span className="text-gray-500 block">Producto</span><span className="font-medium">{ticket.affected_product_name || '-'}</span></div>
              </div>

              {/* Descripción */}
              {ticket.description && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Descripción</h4>
                  <p className="text-sm text-gray-600">{ticket.description}</p>
                </div>
              )}

              {/* Cambiar Estado */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Gestionar</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Estado</label>
                    <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm mt-1">
                      {Object.entries(STATUS_LABELS).map(([v, s]) => (
                        <option key={v} value={v}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Resolución</label>
                    <select value={resolution.type} onChange={e => setResolution(r => ({ ...r, type: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm mt-1">
                      <option value="">-- Sin resolución --</option>
                      {RESOLUTION_TYPES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <textarea value={comment} onChange={e => setComment(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-3" rows={2}
                  placeholder="Agregar comentario o nota de resolución..." />

                <button onClick={handleUpdate} disabled={updating}
                  className="mt-2 w-full py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {updating ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>

              {/* Historial */}
              {ticket.updates?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Historial ({ticket.updates.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {ticket.updates.map((u, i) => (
                      <div key={i} className="flex gap-3 text-xs border-l-2 border-gray-200 pl-3 py-1">
                        <div className="flex-1">
                          <p className="text-gray-800">{u.note}</p>
                          <p className="text-gray-400 mt-0.5">
                            {u.user_name || 'Sistema'} • {new Date(u.created_at).toLocaleString('es-CO')}
                            {u.prev_status && u.new_status && u.prev_status !== u.new_status &&
                              <span className="ml-1 text-blue-500">{u.prev_status} → {u.new_status}</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cerrar */}
              <div className="text-right">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cerrar</button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-12 text-center text-gray-500">PQR no encontrado</div>
        )}
      </div>
    </div>
  );
}
